import { KlaviyoInstanceIdentifierEnum } from '@repo/klaviyo/config'
import { ShopifyInstanceIdentifierEnum } from '@repo/shopify/config'

/**
 * Represents a language with its IANA language tag.
 */
export interface Language {
  tag: Readonly<string> // IANA language tag (e.g., "en", "fr", "es")
  name: Readonly<string> // Human-readable name of the language (e.g., "English", "Dutch")
}

/**
 * Locale identifier (e.g., "en-US", "nl-NL") following BCP 47 standard.
 */
export type LocaleIdentifier = Readonly<Intl.UnicodeBCP47LocaleIdentifier>

/**
 * Country identifier in 2-char ISO-3166 (https://www.iso.org/iso-3166-country-codes.html).
 */
export type CountryIdentifier = Readonly<string>

/**
 * Represents a locale which includes a locale identifier and the associated language.
 *
 * The `id` follows the BCP 47 standard, which combines language and region codes
 * (e.g., "nl-NL" for Dutch as used in the Netherlands).
 */
export interface Locale {
  id: LocaleIdentifier // Locale identifier (e.g., "en-US", "nl-NL") following BCP 47 standard
  name: Readonly<string> // Human-readable name of the locale (e.g., "English (United States)", "Dutch (Netherlands)")
  abbreviation: Readonly<string> // Short human-readable name of the locale (e.g., "EN (US)", "NL")
  language: Language // Language associated with the locale
}

/**
 * Market identifier (e.g., "INT", "UK", "US").
 */
export enum MarketIdentifier {
  INT = 'INT',
  GB = 'GB',
  US = 'US',
}

/**
 * Represents a market which can have multiple locales and a currency.
 */
export interface MarketInstance {
  id: MarketIdentifier // Market identifier (e.g., "INT", "GB", "US")
  name: Readonly<string> // Name of the market (e.g., "International", "United Kingdom", "United States")
  currency: Readonly<string> // Currency code (e.g., "EUR", "GBP", "USD")
  isDefault: Readonly<boolean> // Whether it's the default market/website
  shopifyInstance: ShopifyInstanceIdentifierEnum // Shopify instance for market
  klaviyoInstance: KlaviyoInstanceIdentifierEnum // Klaviyo instance for market
  locales: Locale[] // Locales available in this market
  countries?: CountryIdentifier[] // Primary (e.g. US) and secondary countries (e.g. CA) in this market
}
